import { Entity, formatTicker } from '@api';
import { PriceChangesChart } from '@design/charts/price-changes-chart/PriceChangesChart';
import { EntityHeaderWatchlist } from '@terminal/components/entity-header-watchlist/EntityHeaderWatchlist';
import { NEW_YORK_TIMEZONE } from '@terminal/hooks/use-price-market-reaction-data/usePriceMarketReactionData';
import { EarningsMarketReactionPrice } from '@terminal/modules/market-reaction-live-price/earnings-market-reaction-price/EarningsMarketReactionPrice';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { getDate, getMonth, getYear } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';

import { LineData } from '../types';
import styles from './EarningsMarketReactionChart.module.scss';

export interface EarningsMarketReactionChartProps {
  fromDateEST?: Date;
  toDateEST?: Date;
  line: LineData;
  entity?: Entity;
}

export const EarningsMarketReactionChart = ({
  fromDateEST,
  toDateEST,
  line,
  entity,
}: EarningsMarketReactionChartProps) => {
  const { t } = useTranslation(['insights', 'chart']);

  const { colors } = useChartTheme(['soft']);
  const fillColors = {
    positive: colors.threshold.positive.value,
    negative: colors.threshold.negative.value,
    neutral: colors.threshold.neutral.value,
  };

  if (!entity || !toDateEST) {
    return null;
  }

  const getVerticalLines = () => {
    if (!fromDateEST || !toDateEST) {
      return [];
    }

    return [
      {
        x: fromDateEST,
      },
      {
        x: new Date(
          getYear(fromDateEST),
          getMonth(fromDateEST),
          getDate(fromDateEST),
          16,
          0,
          0,
          0
        ),
        isDashed: true,
      },
      {
        x: new Date(
          getYear(toDateEST),
          getMonth(toDateEST),
          getDate(toDateEST),
          9,
          30,
          0,
          0
        ),
      },
      {
        x: toDateEST,
        isDashed: true,
      },
    ];
  };

  const chartLine = {
    data: line.data.map(item => ({
      date: toZonedTime(new Date(item.time), NEW_YORK_TIMEZONE),
      value: item.value,
    })),
    label: formatTicker(entity),
    color: colors.line.default.value,
  };

  return (
    <div className={styles.container}>
      <div className={styles.chartTitleWrapper}>
        <EntityHeaderWatchlist entity={entity} />
        <EarningsMarketReactionPrice
          entity={entity}
          eventProportionChange={line.lastProportionChange}
          lastKnownPrice={line.lastKnownPrice}
          lastKnownDate={line.lastKnownDate}
          prevCloseData={line.prevCloseData}
        />
      </div>
      <PriceChangesChart
        lines={[chartLine]}
        hasTooltip
        tooltipFormatType={'default2f'}
        fillColors={fillColors}
        yAxisFormatType="default"
        verticalLines={getVerticalLines()}
        prevClosePrice={line.prevCloseData?.close}
        threshold={line.prevCloseData?.close}
        thresholdLabel={
          line.prevCloseData?.time
            ? t('chart:priceChangesClose', {
                date: formatInTimeZone(line.prevCloseData.time, 'UTC', 'MMM d'),
              })
            : undefined
        }
      />
    </div>
  );
};
